import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { MaskLines } from "./Motion";

function TiltCover({ issue }) {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-14, 14]), { stiffness: 120, damping: 18 });
  const gx = useTransform(mx, (v) => `${v * 100}%`);
  const gy = useTransform(my, (v) => `${v * 100}%`);
  const move = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };
  return (
    <div ref={ref} onMouseMove={move} onMouseLeave={reset} className="relative [perspective:1400px]" data-testid="hero-tilt-cover">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 60, rotateZ: -4 }} animate={{ opacity: 1, y: 0, rotateZ: 0 }} transition={{ duration: 1.4, delay: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
        className="relative mx-auto w-[68%] sm:w-[52%] lg:w-[78%] aspect-[2/3] shadow-[0_60px_80px_-40px_rgba(0,0,0,0.55)]">
        <img src={issue?.cover || "/img/bpj-cover-oct26.jpg"} alt={`BPJ ${issue?.title || "latest issue"} cover`} className="absolute inset-0 w-full h-full object-cover" />
        <motion.div className="absolute inset-0 pointer-events-none mix-blend-soft-light"
          style={{ background: useTransform([gx, gy], ([x, y]) => `radial-gradient(circle at ${x} ${y}, rgba(255,255,255,0.75), transparent 55%)`) }} />
        <div className="absolute -right-3 top-6 bottom-6 w-3 bg-gradient-to-r from-black/25 to-transparent [transform:translateZ(-1px)]" />
      </motion.div>
    </div>
  );
}

export default function Hero({ issue }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const big = useTransform(scrollYProgress, [0, 1], [0, -80]);
  return (
    <section ref={ref} className="relative overflow-hidden px-5 lg:px-12 pt-10 lg:pt-14 pb-28 lg:pb-28" data-testid="hero-section">
      <motion.div style={{ y: big }} aria-hidden="true" className="pointer-events-none absolute -right-[6vw] top-6 f-serif italic text-[34vw] lg:text-[26vw] leading-none text-[var(--paper-2)] select-none">14</motion.div>
      <div className="relative grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p className="eyebrow mb-8 flex items-center gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
            <span className="w-8 h-px bg-[var(--red)]" /> {issue ? `Vol. ${issue.volume} No. ${issue.number} · ${issue.season}` : "The latest issue"}
          </motion.p>
          <h1 className="f-serif tracking-[-0.03em] leading-[0.92] text-[3.3rem] sm:text-7xl lg:text-[7.4rem]" data-testid="hero-headline">
            <MaskLines lines={["Performance,", <em key="e" className="font-light">examined</em>, <span key="r">with <span className="text-[var(--red)]">rigour.</span></span>]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-10 max-w-xl text-lg text-[var(--ink-2)] leading-relaxed">
            Business Performance Journal pairs sharp business analysis with double-blind peer-reviewed research — so leaders and scholars can act on evidence, not instinct.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }} className="mt-10 flex flex-wrap gap-3">
            <Link to={`/reader/${issue?.id || "v14n4"}`} className="btn-ink h-12 px-6 inline-flex items-center gap-2 font-semibold" data-testid="hero-read-issue-btn"><BookOpen size={18} /> Read the issue online</Link>
            <Link to="/subscribe" className="btn-line h-12 px-6 inline-flex items-center gap-2 font-semibold" data-testid="hero-subscribe-btn">Find your plan <ArrowUpRight size={18} /></Link>
          </motion.div>
        </div>
        <motion.div style={{ y }} className="lg:col-span-5 relative">
          <div className="absolute inset-x-[8%] -inset-y-6 lg:inset-y-[-8%] bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.08),transparent_65%)]" />
          <TiltCover issue={issue} />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }} className="mt-8 text-center">
            <p className="eyebrow">In this issue</p>
            <p className="f-serif text-2xl mt-1 italic">{issue?.title || "The Productivity Paradox"}</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
